import clsx from "clsx";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";

/** Interne Kalkulations-Checkliste für EXC2-Projekte (nicht kundenrelevant). */
export const EXC2_KALKULATION_HINWEISE = [
  "Statische Berechnung notwendig",
  "Werkszeugnisse erforderlich (ca. 15€ pro Sorte)",
  "Prüfung durch Technik ob wir das bauen dürfen",
  "Zerstörungsfreie Prüfung notwendig? – Rücksprache mit Technik",
] as const;

export type Exc2Choice = "ja" | "nein";

export function Exc2KalkulationHinweisAlert({ className }: { className?: string }) {
  return (
    <Alert variant="warning" title="EXC2 – bei der Kalkulation beachten und einpreisen" className={className}>
      <ul className="mt-1 list-disc space-y-0.5 pl-5">
        {EXC2_KALKULATION_HINWEISE.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </Alert>
  );
}

export function isProjectExc2(executionClass: string | null | undefined): boolean {
  return String(executionClass || "").trim().toUpperCase() === "EXC2";
}

function exc2AnswerStorageKey(docId: number): string {
  return `eh-gd-exc2-antwort:${docId}`;
}

export function loadExc2Answer(docId: number): Exc2Choice | null {
  try {
    const raw = sessionStorage.getItem(exc2AnswerStorageKey(docId));
    if (raw === "ja" || raw === "nein") return raw;
  } catch {
    /* ignore */
  }
  return null;
}

export function saveExc2Answer(docId: number, choice: Exc2Choice): void {
  try {
    sessionStorage.setItem(exc2AnswerStorageKey(docId), choice);
  } catch {
    /* ignore */
  }
}

export interface Exc2FrageBlockProps {
  loading?: boolean;
  saving?: boolean;
  projectIsExc2: boolean;
  choice: Exc2Choice | null;
  editable: boolean;
  onChange: (choice: Exc2Choice) => void;
  className?: string;
}

/** EXC2-Nachfrage für Angebots-Kopfdaten. */
export function Exc2FrageBlock({
  loading = false,
  saving = false,
  projectIsExc2,
  choice,
  editable,
  onChange,
  className,
}: Exc2FrageBlockProps) {
  const effectiveChoice: Exc2Choice | null = projectIsExc2 ? "ja" : choice;
  const unanswered = !effectiveChoice;

  return (
    <div
      className={clsx(
        "rounded-lg border px-3 py-3",
        unanswered
          ? "border-status-orange/50 bg-[color-mix(in_srgb,_var(--eh-orange)_12%,_var(--eh-bg-card))]"
          : "border-separator bg-card",
        className,
      )}
    >
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-label-secondary">
          <Spinner className="h-4 w-4" />
          EXC-Status wird geprüft …
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-medium text-label">Ist das Projekt ein EXC2-Projekt?</p>
            {unanswered ? (
              <span className="text-xs font-medium text-status-orange">Pflicht vor Erstellen</span>
            ) : null}
          </div>
          {projectIsExc2 ? (
            <p className="text-sm text-label-secondary">
              Das Projekt ist als EXC2 markiert (z. B. über die Stückliste oder diese Auswahl).
            </p>
          ) : editable ? (
            <>
              <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="EXC2-Projekt">
                {(
                  [
                    ["nein", "Nein"],
                    ["ja", "Ja"],
                  ] as const
                ).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={effectiveChoice === value}
                    disabled={saving}
                    onClick={() => onChange(value)}
                    className={clsx(
                      "rounded-full px-3 py-1.5 text-sm font-medium",
                      effectiveChoice === value
                        ? "bg-accent text-white"
                        : "bg-fill-tertiary text-label-secondary hover:text-label",
                      saving && "cursor-not-allowed opacity-70",
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {!effectiveChoice ? (
                <p className="text-xs text-label-secondary">
                  Bitte Ja oder Nein wählen. Die Antwort wird mit dem Entwurf gespeichert. Bei Ja
                  wird zusätzlich die EXC2-Markierung am Projekt gesetzt (wie in der Stückliste).
                </p>
              ) : null}
            </>
          ) : (
            <p className="text-sm text-label-secondary">
              {effectiveChoice === "ja"
                ? "Als EXC2-Projekt beantwortet."
                : effectiveChoice === "nein"
                  ? "Nicht als EXC2-Projekt beantwortet."
                  : "Noch nicht beantwortet."}
            </p>
          )}
          {effectiveChoice === "ja" ? <Exc2KalkulationHinweisAlert /> : null}
        </div>
      )}
    </div>
  );
}
